import React, { useCallback, useRef, useState } from "react";
import { WEBGL } from "./utils/webGL.utils";
import Main3DManager from "./main.3d.manager";
import "./styles/art.space.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useEvents from "../../hooks/useEvents";
import { useSelector } from "react-redux";
import { AppState } from "../../store/root.reducer";
import { ApiClient } from "../../utils/apiClient";
import User from "../../models/user.model";
import { ToastContainer, toast } from "react-toastify";
import { RenderSettingsModal } from "../modals/render.settings";
import { resolveActiveMoodIndex } from "../../utils/moodVisibility";
import { resolveSavedMoodPersistence } from "./gallery.bootstrap";

import AppData from "./app.data";
import { Slot } from "@/types/slotsData";
import {
  buildTelemetrySpaceContext,
  trackArtworkView,
  trackGalleryView,
  type TelemetrySpaceContext,
} from "../../utils/telemetry";
const appData = new AppData()

const BASE_API = process.env.REACT_APP_BASE_API;
const apiClient = ApiClient(BASE_API);

const AdminMoMComponent = React.memo(() => {
  const { id } = useParams()
  const user: User = useSelector((state: AppState) => state.auth.user);
  appData.setUserData(user)
  const navigate = useNavigate();
  const location = useLocation();
  const canvasRef = useRef(null);
  const { listedVouchers } = useSelector((state: AppState) => state.nft);
  const galleryTelemetryContextRef = useRef<TelemetrySpaceContext>({});
  const loadedGalleryIdRef = useRef<string | null>(null);
  const trackedGalleryViewRef = useRef<string | null>(null);
  const [renderSettingsModal, setRenderSettingsModal] = useState<boolean>(false);
  const [moods, setMoods] = useState<any[]>([]);

  const [main3DManager, setMain3DManager] = React.useState<
    Main3DManager | undefined
  >();

  const hideLoadingScreen = () => {
    const loadingText = document.getElementById("loading");
    if (loadingText) {
      loadingText.style.opacity = "0.0";
      loadingText.style.pointerEvents = "none";
    }
  };

  // Add file handling methods
  const getFilesFromFolder = async (folder: string): Promise<string[]> => {
    const normalizedFolder = folder.replace(/\/+$/, "");
    return ApiClient(BASE_API).get3DFileUrls(normalizedFolder);
  };

  const uploadFilesToFolder = async (folder: string, files: File[]): Promise<{ name: string, url: string }[]> => {
    const normalizedFolder = folder.replace(/\/+$/, "");
    return new Promise((resolve, reject) => {
      ApiClient(BASE_API).upload3Dfiles(normalizedFolder, files).then((result: any) => {
        resolve(result)
      })
    })
  };

  const successNotify = (msg: any) => {
    toast.success(msg, {
      position: "bottom-left",
    })
  }

  const failedNotify = (msg: string) => {
    toast.error(msg, {
      position: "bottom-left",
    })
  }

  const onDioramaButton = useCallback((slot: Slot) => {
    try {
      const selectedArtwork = slot.artworkWrapper.artworks[slot.artworkWrapper.activeArtwork]
      navigate(`/gallery/${selectedArtwork.galleryId}?t=admin`)
    } catch (e) {
      console.log(e); // Logs the error
    }
  }, []);

  const onArtworkClicked = useCallback((userData: any) => {
    const wrapper = userData?.artworkWrapper || userData;
    const activeArtworkIndex = Number(wrapper?.activeArtwork);
    const selectedArtwork = wrapper?.artworks?.[Number.isFinite(activeArtworkIndex) ? activeArtworkIndex : 0] || userData?.artwork;
    if (selectedArtwork?._id && loadedGalleryIdRef.current) {
      trackArtworkView(
        String(selectedArtwork._id),
        `${location.pathname}${location.search || ""}`,
        loadedGalleryIdRef.current,
        "admin",
        galleryTelemetryContextRef.current
      );
    }
    console.log("Clicked on artwork with data: ", userData)
  }, [location.pathname, location.search]);

  React.useEffect(() => {
    let _main3D = new Main3DManager();
    appData.setMain3DManager(_main3D);

    setMain3DManager(_main3D);

    return () => {
      _main3D.destroy();
      appData.setMain3DManager(null);
    };
  }, [user]);

  React.useEffect(() => {
    if (main3DManager && canvasRef.current) {
      main3DManager.init(canvasRef.current);
      main3DManager.setDioramaClickEvent(onDioramaButton);
      main3DManager.setArtworkClickEvent(onArtworkClicked);
    }
  }, [main3DManager, canvasRef, onArtworkClicked, onDioramaButton]);

  useEvents(main3DManager, canvasRef, listedVouchers);

  React.useEffect(() => {
    let isCancelled = false;

    const apiClient = ApiClient(BASE_API);
    if (main3DManager && WEBGL.isWebGLAvailable() && user?._id) {
      const init3D = async () => {
        try {
          // Backend can intermittently return unauthorized during local startup.
          let galleryResp = await apiClient.getDefaultMoMGalleryByAdmin();
          if (isCancelled) return;
          if (!galleryResp?.status) {
            await new Promise((resolve) => setTimeout(resolve, 400));
            if (isCancelled) return;
            galleryResp = await apiClient.getDefaultMoMGalleryByAdmin();
            if (isCancelled) return;
          }

          const gallery = galleryResp?.status ? galleryResp.gallery : null;
          if (!gallery?.spaceId) {
            hideLoadingScreen();
            failedNotify("Failed to load gallery data. Please refresh and sign in again.");
            return;
          }

          appData.setGalleryData(gallery);

          const spaceResp = await apiClient.getSpaceById(gallery.spaceId);
          if (isCancelled) return;
          const space = spaceResp?.status ? spaceResp.space : null;
          if (!space) {
            hideLoadingScreen();
            failedNotify("Failed to load space data.");
            return;
          }

          appData.setSpaceData(space);
          const telemetryContext = buildTelemetrySpaceContext(gallery, space);
          galleryTelemetryContextRef.current = telemetryContext;
          loadedGalleryIdRef.current = gallery?._id ? String(gallery._id) : null;
          const galleryTelemetryKey = `${loadedGalleryIdRef.current}:${location.pathname}${location.search || ""}`;
          if (loadedGalleryIdRef.current && trackedGalleryViewRef.current !== galleryTelemetryKey) {
            trackedGalleryViewRef.current = galleryTelemetryKey;
            trackGalleryView(
              loadedGalleryIdRef.current,
              `${location.pathname}${location.search || ""}`,
              "admin",
              telemetryContext
            );
          }
          setMoods(space.data?.moods || []);

          const fallbackMood =
            Number.isInteger(gallery?.data?.mood) ? gallery.data.mood : undefined;
          const resolvedMood = resolveActiveMoodIndex(
            space.data?.moods,
            fallbackMood,
            {
              userId: user?._id,
              galleryId: gallery?._id,
              isAdmin: true,
              includeUnpublished: true
            }
          );

          if (!gallery.data) {
            gallery.data = {} as any;
          }
          gallery.data.mood = resolvedMood;
          appData.setGalleryData(gallery);

          console.log("%cLoading data: ", "color:red", appData);
          if (isCancelled) return;
          await main3DManager.loadGallery();
        } catch (error) {
          if (isCancelled) return;
          console.error("AdminMoM: Error initializing 3D scene", error);
          hideLoadingScreen();
          failedNotify("Failed to initialize 3D scene.");
        }
      }
      init3D()
    }

    return () => {
      isCancelled = true;
      if (main3DManager) {
        main3DManager.dispose();
      }
    };
  }, [location.pathname, location.search, main3DManager, user?._id]);

  const onChangeMood = (newMood: any, local?: any) => {
    console.log(local)
    main3DManager.changeMood(newMood, local);
  }

  const onSelectMoodForGallery = async (moodID: number): Promise<boolean> => {
    const galleryId = appData.galleryData?._id;
    if (!galleryId) return false;

    const resp = await apiClient.changeMoodForGallery(galleryId, moodID);
    const persisted = Boolean(resp?.status);
    if (!persisted) return false;

    if (!appData.galleryData.data) {
      appData.galleryData.data = {} as any;
    }
    appData.galleryData.data.mood = moodID;
    appData.setGalleryData(appData.galleryData);
    return true;
  }



  const onApplyMood = async (moodID: number, data: any) => {
    console.log("onApplyMood", moodID, data);
    const galleryData = appData.galleryData;
    const response = await apiClient.upsertMood(galleryData.spaceId, data, moodID);

    if (response?.status) {
      const persisted = resolveSavedMoodPersistence(
        appData.spaceData?.data?.moods,
        moodID,
        data,
        response
      );
      const spaceData = {
        ...appData.spaceData,
        data: {
          ...appData.spaceData.data,
          moods: persisted.moods,
        },
      };
      appData.setSpaceData(spaceData);
      setMoods(persisted.moods);

      let moodSelectionPersisted = true;
      if (galleryData?._id) {
        const moodSelectionResp = await apiClient.changeMoodForGallery(galleryData._id, persisted.moodIndex);
        moodSelectionPersisted = Boolean(moodSelectionResp?.status);
      }

      if (moodSelectionPersisted) {
        if (!galleryData.data) {
          galleryData.data = {} as any;
        }
        galleryData.data.mood = persisted.moodIndex;
        appData.setGalleryData(galleryData);
        successNotify("Mood has been modified");
      } else {
        failedNotify("Mood settings were saved, but failed to set the selected mood for this gallery.");
      }
    } else {
      failedNotify("Failed to save mood setting.");
    }
  }

  return (
    <>
      <div id="WebGL" draggable="false" ref={canvasRef}></div>
      {/* <div id="prompt-text"></div> */}
      <div id="loading" className="loading-screen">Loading ...</div>
      <div id="joystick-background"></div>
      <div id="joystick-thumb"></div>
      <button className="btn-custom position-absolute" style={{ top: "100px", left: "20px" }} onClick={() => {
        setRenderSettingsModal(true);
      }}>
        Render Settings
      </button>
      {renderSettingsModal && <RenderSettingsModal
        show={renderSettingsModal}
        onHide={() => {
          setRenderSettingsModal(false);
        }}
        currentMood={moods[appData.galleryData.data.mood]}
        onChangeMood={onChangeMood}
        onSelectMoodForGallery={onSelectMoodForGallery}
        appData={appData}
        onGetFilesFromFolder={getFilesFromFolder}
        onUploadFilesToFolder={uploadFilesToFolder}
        onApplyMood={onApplyMood}
        failedNotify={failedNotify}
      />}
      <ToastContainer />
    </>
  );
});

export { AdminMoMComponent };
